import { MutableRefObject } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { gql, useMutation } from '@apollo/client';
import classNames from 'classnames';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { Scope } from 'common/graphqlTypes';
import { processServerSettings, ServerSettings } from 'common/util/settings';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { isSidebarToggleVisibleState } from 'components/search/SearchLogsPage/SearchStateManager.state';

import IconButton from 'components/common/IconButton/IconButton';
import PlayArrow from '@mui/icons-material/PlayArrow';
import './SidebarToggle.scss';

const SET_SHOW_SIDEBAR = gql`
  mutation setShowSidebar($scope: Scope!, $settings: DisplaySettingsInput!) {
    setDisplaySettings(scope: $scope, settings: $settings) {
      showLeftNavExpanded
    }
  }
`;

type SidebarToggleProps = {
  toggleRef: MutableRefObject<HTMLDivElement | null>;
};

/**
 * This component is a thin vertical strip between the sidebar and the search results area that has an icon
 * that can be used to hide or show the sidebar.
 */
function SidebarToggle({ toggleRef }: SidebarToggleProps): JSX.Element {

  const [ settings, setSettings ] = useRecoilState(settingsState);
  const [ setShowSidebar ] = useMutation(SET_SHOW_SIDEBAR);
  const isSidebarToggleVisible = useRecoilValue(isSidebarToggleVisibleState);

  const showSidebar = settings[K.SETTING.SHOW_SIDEBAR];
  const toggleSidebar = (): void => {
    const newValue = !showSidebar;
    const serverSettingName = K.SERVER_SETTING_NAME[K.SETTING.SHOW_SIDEBAR];
    // update client optimistically; that avoids a visual delay in collapsing/expanding the sidebar,
    // as well as a React warning about updating an unmounted component (LogLine, whose layout is affected by sidebar)
    setSettings(processServerSettings(settings, { [serverSettingName]: newValue } as unknown as ServerSettings));

    // update setting in session
    setShowSidebar({ variables: { scope: Scope.session, settings: { showLeftNavExpanded: newValue } } });
  };

  const buttonClass = classNames('Sidebar__IconButton', {
    'Sidebar__IconButton--Hide': showSidebar,
    'Sidebar__IconButton--Show': !showSidebar,
  });
  const containerClass = classNames({ 'IconButton__OverlayWrapper--DisplayNone' : !isSidebarToggleVisible });
  const sidebarToggleClass = classNames('SidebarToggle', {
    'SidebarToggle--NoBorder': !isSidebarToggleVisible,
  });

  return (
    <div className={sidebarToggleClass} ref={toggleRef}>
      <IconButton
        tooltip={showSidebar ? 'Hide sidebar' : 'Show sidebar'}
        className={buttonClass}
        containerClassName={containerClass}
        onClick={(): void => toggleSidebar()}
        icon={<PlayArrow />}
        testId={ELEMENT.searchSidebarToggle}
      />
    </div>
  );
}

export default SidebarToggle;
